//* Student growth diary write screen (create diary with media uploads)
//* MISTAKE: Missing required purpose header; add it first when editing/creating files.
import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentLanguage, getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import {
    getImagePresignedUrl,
    getVideoPresignedUrl,
    updateVideoThumbnail,
    uploadImage,
    uploadVideoWithThumbnail
} from "api/common/Data.api";
import { createDiary, IDiaryCreateDataResult } from "api/student/v2/diary.api";
import VideoUploadFlowPopup, { VideoUploadFlowPhase } from "commons/popup/VideoUploadFlowPopup.component";

interface DiaryFormData {
    title: string;
    content: string;
    images: Array<{ id: string; url: string; file: File }>;
    video: File | null;
}

const DEFAULT_VIDEO_POPUP_PHASE: VideoUploadFlowPhase = "uploading";

function dataUrlToFile(dataUrl: string, fileName: string): File | null {
    try {
        const [meta, base64] = dataUrl.split(",");
        if (!meta || !base64) return null;
        const mimeMatch = /data:(.*?);base64/.exec(meta);
        const mime = mimeMatch?.[1] || "image/jpeg";
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
        return new File([bytes], fileName, { type: mime });
    } catch {
        return null;
    }
}

async function extractVideoThumbnailCandidates(videoUrl: string, count = 20): Promise<string[]> {
    try {
        const video = document.createElement("video");
        video.preload = "auto";
        video.muted = true;
        video.playsInline = true;
        video.crossOrigin = "anonymous";
        video.src = videoUrl;
        video.load();

        await new Promise<void>((resolve, reject) => {
            let done = false;
            const finish = (error?: Error) => {
                if (done) return;
                done = true;
                video.onloadedmetadata = null;
                video.onloadeddata = null;
                video.onerror = null;
                if (error) reject(error);
                else resolve();
            };
            const timer = window.setTimeout(() => finish(new Error("metadata load timeout")), 5000);
            video.onloadedmetadata = () => {
                window.clearTimeout(timer);
                finish();
            };
            video.onloadeddata = () => {
                window.clearTimeout(timer);
                finish();
            };
            video.onerror = () => {
                window.clearTimeout(timer);
                finish(new Error("failed to load metadata"));
            };
        });

        const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 1;
        const times = Array.from({ length: Math.max(1, count) }, (_, idx) => {
            if (count <= 1) return 0;
            const ratio = idx / (count - 1);
            return Math.min(duration - 0.01, Math.max(0, duration * ratio));
        });

        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, video.videoWidth || 640);
        canvas.height = Math.max(1, video.videoHeight || 360);
        const ctx = canvas.getContext("2d");
        if (!ctx) return [];

        const thumbs: string[] = [];
        for (const t of times) {
            await new Promise<void>((resolve) => {
                let done = false;
                const finish = () => {
                    if (done) return;
                    done = true;
                    video.onseeked = null;
                    video.onerror = null;
                    resolve();
                };
                const timer = window.setTimeout(finish, 1500);
                video.onseeked = () => {
                    window.clearTimeout(timer);
                    finish();
                };
                video.onerror = () => {
                    window.clearTimeout(timer);
                    finish();
                };
                try {
                    if (Math.abs(video.currentTime - t) < 0.01) {
                        window.clearTimeout(timer);
                        finish();
                        return;
                    }
                    video.currentTime = t;
                } catch {
                    window.clearTimeout(timer);
                    finish();
                }
            });
            try {
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                thumbs.push(canvas.toDataURL("image/jpeg", 0.88));
            } catch {
                // ignore failed frame
            }
        }
        return thumbs.filter(Boolean);
    } finally {
        // no-op
    }
}

async function extractVideoThumbnailCandidatesFromFile(file: File, count = 20): Promise<string[]> {
    const objectUrl = URL.createObjectURL(file);
    try {
        return await extractVideoThumbnailCandidates(objectUrl, count);
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
}

function isIosClient(): boolean {
    const ua = navigator.userAgent || "";
    const iOSPattern = /iPad|iPhone|iPod/i.test(ua);
    const iPadOs13Up =
        /Macintosh/i.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;
    return iOSPattern || iPadOs13Up;
}

const WriteDiary: React.FC = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const videoInputRef = useRef<HTMLInputElement>(null);
    const hasTriggeredPreUploadRef = useRef(false);
    const hasShownUnusableVideoAlertRef = useRef(false);

    const [videoWidth, setVideoWidth] = useState(0);
    const [videoHeight, setVideoHeight] = useState(0);
    const [isVideoPreUploading, setIsVideoPreUploading] = useState(false);
    const [videoPreUploadProgress, setVideoPreUploadProgress] = useState(0);
    const [uploadedVideoId, setUploadedVideoId] = useState<string | null>(null);
    const [uploadedVideoPreviewUrl, setUploadedVideoPreviewUrl] = useState<string | null>(null);

    const [formData, setFormData] = useState<DiaryFormData>({
        title: "",
        content: "",
        images: [],
        video: null,
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasBlobPreviewError, setHasBlobPreviewError] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({
        isVisible: false,
        phase: DEFAULT_VIDEO_POPUP_PHASE as VideoUploadFlowPhase,
        currentImageIndex: 0,
        totalImages: 0,
        isVideoUploading: false,
        videoUploadProgress: 0,
        currentStep: '',
        thumbnails: [] as string[],
        selectedIndex: 0,
        errorMessage: "",
        isSaving: false,
    });
    const currentLanguage = getCurrentLanguage();
    const text = useMemo(
        () => ({
            writeTitle: getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.writeTitle") || "성장 다이어리 작성",
            writeDesc:
                getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.writeDesc") ||
                "오늘의 연습과 성장 과정을 기록해보세요. 캐스팅 매니저님들이 여러분의 성장 과정을 지켜볼 수 있습니다.",
            title: getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtTitle") || "제목",
            content: getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtContent") || "내용",
            titlePlaceholder:
                getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtTitlePlaceholder") || "오늘의 제목을 입력하세요",
            contentPlaceholder:
                getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtContentPlaceholder") ||
                "오늘 연습한 내용, 느낀 점, 어려웠던 부분 등을 자유롭게 작성해주세요",
            videoAttachment: getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtVideoAttachment") || "영상 첨부",
            videoCount: getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtVideoCount") || "(최대 1개)",
            videoUploadHint:
                getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtVideoUploadHint") ||
                "클릭하여 영상을 선택하거나 드래그하여 업로드하세요",
            videoUploading:
                getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtUploadProgress.txtVideoUploading") ||
                "영상 업로드 중...",
            imageAttachment: getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtImageAttachment") || "이미지 첨부",
            imageUploadHint:
                getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtImageUploadHint") ||
                "클릭하여 이미지를 선택하거나 드래그하여 업로드하세요",
            validationMediaAlert:
                getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtValidationMediaAlert") ||
                "영상이나 이미지 중 하나는 추가해주세요 :)",
            safetyNotice:
                getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtSafetyNotice") ||
                "여러분의 안전한 캐스팅 환경을 위해 개인의 연락처나 SNS주소를 입력할 경우 다이어리가 감춰지거나 삭제될 수 있어요.",
            btnCancel: getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.btnCancel") || "취소",
            btnSaving: getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.btnSaving") || "저장 중...",
            btnSave: getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.btnSave") || "다이어리 저장",
            popupClose: getLanguageValue(PAGE_TYPE.COMMON, "btn.txtClose") || "닫기",
            popupSave: getLanguageValue(PAGE_TYPE.COMMON, "btn.txtSave") || "저장",
            popupPleaseWait:
                getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtUploadProgress.txtPleaseWait") ||
                "잠시만 기다려주세요...",
            popupThumbnailTitle:
                getLanguageValue(PAGE_TYPE.STUDENT, "agencyProfile.uploadIntroVideoThumbnailTitle") || "썸네일 선택",
        }),
        [currentLanguage]
    );
    const showUnusableVideoAlertOnce = () => {
        if (hasShownUnusableVideoAlertRef.current) {
            return;
        }
        hasShownUnusableVideoAlertRef.current = true;
        alert(
            getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtInvalidSelectableVideoFile") ||
                "업로드 할 수 없는 파일입니다"
        );
    };

    const handleInputChange = (field: keyof DiaryFormData, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files) return;

        if (formData.images.length + files.length > 10) {
            alert(getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtImageUploadLimit") || "이미지는 최대 10개까지만 업로드 가능합니다.");
            return;
        }

        // 이미지 파일들을 순서대로 처리하기 위해 Promise 배열 생성
        const imagePromises = Array.from(files).map(file => {
            return new Promise<{ id: string; url: string; file: File }>((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    if (e.target?.result) {
                        resolve({
                            id: Math.random().toString(36).substr(2, 9),
                            url: e.target.result as string,
                            file: file
                        });
                    }
                };
                reader.readAsDataURL(file);
            });
        });

        // 모든 이미지가 로드되면 순서대로 상태에 추가
        Promise.all(imagePromises).then(newImages => {
            const updatedImages = [...formData.images, ...newImages];
            handleInputChange("images", updatedImages);
        }).catch(error => {
            console.error("이미지 로드 실패:", error);
            alert(getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtImageLoadError") || "이미지를 불러오는데 실패했습니다. 다시 시도해주세요.");
        });

        // 파일 input 초기화 (같은 파일을 다시 선택할 수 있도록)
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveImage = (id: string) => {
        handleInputChange("images", formData.images.filter(image => image.id !== id));
    };

    const uploadVideoForUnsupportedPreview = async (file: File, width: number, height: number) => {
        setIsVideoPreUploading(true);
        setVideoPreUploadProgress(0);
        let shouldKeepPopup = false;
        setUploadProgress(prev => ({
            ...prev,
            isVisible: true,
            phase: "uploading",
            isVideoUploading: true,
            videoUploadProgress: 0,
            currentStep: getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtUploadProgress.txtVideoUploading") || "영상 업로드 중..."
        }));
        try {
            const response = await uploadVideoWithThumbnail(
                file,
                width,
                height,
                false,
                undefined,
                true,
                (progressEvent) => {
                    const total = progressEvent.total || file.size || 1;
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / total);
                    setVideoPreUploadProgress(percentCompleted);
                    setUploadProgress(prev => ({
                        ...prev,
                        videoUploadProgress: percentCompleted
                    }));
                }
            );

            if (!response?.id) {
                throw new Error("영상 업로드 응답에 id가 없습니다.");
            }
            setUploadProgress(prev => ({
                ...prev,
                currentStep:
                    getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtUploadProgress.txtThumbnailLoading") ||
                    "썸네일을 가져오는 중 입니다.",
            }));

            setUploadedVideoId(response.id);
            const presigned = await getVideoPresignedUrl(response.id);
            if (presigned?.result) {
                setUploadedVideoPreviewUrl(presigned.result);
            } else {
                throw new Error("영상 미리보기 URL을 가져오지 못했습니다.");
            }
            const useLocalFirst = isIosClient();
            let thumbnails = useLocalFirst
                ? await extractVideoThumbnailCandidatesFromFile(file, 20)
                : await extractVideoThumbnailCandidates(presigned.result, 20);
            if (!thumbnails.length) {
                thumbnails = useLocalFirst
                    ? await extractVideoThumbnailCandidates(presigned.result, 20)
                    : await extractVideoThumbnailCandidatesFromFile(file, 20);
            }
            if (!thumbnails.length && (response as any)?.thumbnailImageId) {
                try {
                    const thumbnailInfo = await getImagePresignedUrl((response as any).thumbnailImageId);
                    if (thumbnailInfo?.result) {
                        thumbnails = [thumbnailInfo.result];
                    }
                } catch {
                    // keep empty and let error flow below handle it
                }
            }
            if (!thumbnails.length) {
                setUploadProgress(prev => ({
                    ...prev,
                    phase: "error",
                    errorMessage: getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtVideoUploadError") || "영상 업로드에 실패했습니다.",
                    isVideoUploading: false,
                }));
                shouldKeepPopup = true;
                return;
            }
            setUploadProgress(prev => ({
                ...prev,
                phase: "selecting",
                thumbnails,
                selectedIndex: 0,
                currentStep: "",
                isVideoUploading: false,
                isSaving: false,
            }));
            shouldKeepPopup = true;
        } catch (error) {
            console.error("미리보기 대체용 영상 업로드 실패:", error);
            hasTriggeredPreUploadRef.current = false;
            showUnusableVideoAlertOnce();
            setUploadProgress(prev => ({
                ...prev,
                phase: "error",
                errorMessage: getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtVideoUploadError") || "영상 업로드에 실패했습니다.",
                isVideoUploading: false,
            }));
            shouldKeepPopup = true;
        } finally {
            setIsVideoPreUploading(false);
            if (!shouldKeepPopup) {
                setUploadProgress(prev => ({
                    ...prev,
                    isVisible: false,
                    isVideoUploading: false,
                    videoUploadProgress: 0
                }));
            }
        }
    };

    const triggerPreUploadFallback = () => {
        if (!formData.video || uploadedVideoId || isVideoPreUploading || hasTriggeredPreUploadRef.current) {
            return;
        }
        hasTriggeredPreUploadRef.current = true;
        uploadVideoForUnsupportedPreview(formData.video, videoWidth, videoHeight);
    };

    const handleVideoPreviewError = (videoElement?: HTMLVideoElement | null) => {
        setHasBlobPreviewError(true);
        // Stop browser retries for broken blob URL immediately.
        if (videoElement) {
            videoElement.pause();
            videoElement.removeAttribute("src");
            videoElement.load();
        }
    };

    const handleVideoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        const videoInput = event.target;
        if (file) {
            if (file.type.startsWith('video/')) {
                setUploadProgress(prev => ({
                    ...prev,
                    isVisible: true,
                    currentStep: getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtUploadProgress.txtVideoUploadReady") || "영상 업로드 준비 중...",
                    isVideoUploading: false,
                    videoUploadProgress: 0
                }));
                hasShownUnusableVideoAlertRef.current = false;
                // 영상 길이 확인을 위한 비디오 요소 생성
                const video = document.createElement('video');
                video.preload = 'metadata';
                const metadataUrl = URL.createObjectURL(file);

                video.onloadedmetadata = () => {
                    URL.revokeObjectURL(metadataUrl);
                    // 영상 길이를 초 단위로 확인 (5분 = 300초)
                    if (video.duration > 300) {
                        alert(getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtVideoDurationLimit") || "영상은 최대 5분까지만 업로드 가능합니다.");
                        setUploadProgress(prev => ({ ...prev, isVisible: false }));
                        videoInput.value = '';
                        return;
                    }

                    setVideoWidth(video.videoWidth);
                    setVideoHeight(video.videoHeight);
                    // 5분 이하인 경우에만 영상 파일 설정
                    setUploadedVideoId(null);
                    setUploadedVideoPreviewUrl(null);
                    setVideoPreUploadProgress(0);
                    setHasBlobPreviewError(false);
                    hasTriggeredPreUploadRef.current = false;
                    handleInputChange("video", file);
                    uploadVideoForUnsupportedPreview(file, video.videoWidth, video.videoHeight);
                };

                video.onerror = () => {
                    URL.revokeObjectURL(metadataUrl);
                    // 메타데이터 로드 실패 시에도 fallback 업로드를 시도한다.
                    setVideoWidth(0);
                    setVideoHeight(0);
                    setUploadedVideoId(null);
                    setUploadedVideoPreviewUrl(null);
                    setVideoPreUploadProgress(0);
                    hasTriggeredPreUploadRef.current = false;
                    setHasBlobPreviewError(true);
                    handleInputChange("video", file);
                    uploadVideoForUnsupportedPreview(file, 0, 0);
                };

                // 영상 파일의 URL을 설정하여 메타데이터 로드
                video.src = metadataUrl;
            } else {
                showUnusableVideoAlertOnce();
                setUploadProgress(prev => ({ ...prev, isVisible: false }));
                videoInput.value = '';
            }
        }
    };

    const handleRemoveVideo = () => {
        hasTriggeredPreUploadRef.current = false;
        setVideoWidth(0);
        setVideoHeight(0);
        setIsVideoPreUploading(false);
        setVideoPreUploadProgress(0);
        setUploadedVideoId(null);
        setUploadedVideoPreviewUrl(null);
        setHasBlobPreviewError(false);
        hasShownUnusableVideoAlertRef.current = false;
        setUploadProgress(prev => ({ ...prev, isVisible: false }));
        handleInputChange("video", null);
    };

    const handleSelectVideoThumbnail = (index: number) => {
        setUploadProgress(prev => ({ ...prev, selectedIndex: index }));
    };

    const handleCloseVideoUploadPopup = () => {
        setUploadProgress(prev => ({ ...prev, isVisible: false }));
    };

    const handleSaveVideoThumbnail = async () => {
        if (!uploadedVideoId) {
            setUploadProgress(prev => ({
                ...prev,
                phase: "error",
                errorMessage: getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtVideoUploadError") || "영상 업로드에 실패했습니다.",
            }));
            return;
        }
        const selectedDataUrl = uploadProgress.thumbnails[uploadProgress.selectedIndex];
        const thumbnailFile = selectedDataUrl
            ? dataUrlToFile(selectedDataUrl, `diary-video-thumb-${Date.now()}.jpg`)
            : null;
        if (!thumbnailFile) {
            setUploadProgress(prev => ({
                ...prev,
                phase: "error",
                errorMessage: getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtVideoUploadError") || "영상 업로드에 실패했습니다.",
            }));
            return;
        }
        try {
            setUploadProgress(prev => ({ ...prev, isSaving: true }));
            const uploadResult = await uploadImage(thumbnailFile, true);
            if (uploadResult?.id) {
                try {
                    await updateVideoThumbnail(uploadedVideoId, uploadResult.id);
                } catch (e: any) {
                    // backward compatibility for environments without new endpoint
                    if (e?.response?.status !== 404) throw e;
                }
            }
            setUploadProgress(prev => ({ ...prev, isVisible: false, isSaving: false }));
        } catch (e) {
            setUploadProgress(prev => ({
                ...prev,
                phase: "error",
                isSaving: false,
                errorMessage: getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtVideoUploadError") || "영상 업로드에 실패했습니다.",
            }));
        }
    };

    // 비디오 URL을 별도 state로 관리하여 안정성 확보
    const [videoUrl, setVideoUrl] = useState<string | null>(null);

    // 비디오 파일이 변경될 때만 URL 생성
    useEffect(() => {
        if (formData.video) {
            const newUrl = URL.createObjectURL(formData.video);
            setVideoUrl(newUrl);
            setHasBlobPreviewError(false);

            // 이전 URL이 있다면 정리
            return () => {
                URL.revokeObjectURL(newUrl);
            };
        } else {
            setVideoUrl(null);
        }
    }, [formData.video]);

    // 컴포넌트 언마운트 시 비디오 URL 정리
    useEffect(() => {
        return () => {
            if (videoUrl) {
                URL.revokeObjectURL(videoUrl);
            }
        };
    }, []);



    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.title.trim() || !formData.content.trim()) {
            alert(getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtValidationTitleContent") || "제목과 내용을 입력해주세요.");
            return;
        }

        if (!formData.video && !uploadedVideoId && formData.images.length === 0) {
            alert(getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtValidationMedia") || "영상 또는 이미지를 하나 이상 첨부해주세요.");
            return;
        }

        if (isVideoPreUploading) {
            return;
        }

        setIsSubmitting(true);

        // 업로드 진행 상황 팝업 표시
        setUploadProgress({
            isVisible: true,
            phase: "uploading",
            currentImageIndex: 0,
            totalImages: formData.images.length,
            isVideoUploading: false,
            videoUploadProgress: 0,
            currentStep: formData.images.length > 0 ?
                (getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtUploadProgress.txtImageUploadReady") || '이미지 업로드 준비 중...') :
                (uploadedVideoId
                    ? (getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtUploadProgress.txtDiarySaving") || '다이어리 저장 중...')
                    : (getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtUploadProgress.txtVideoUploadReady") || '영상 업로드 준비 중...')),
            thumbnails: [],
            selectedIndex: 0,
            errorMessage: "",
            isSaving: false,
        });

        try {
            // 1. 이미지 파일들을 순차적으로 업로드
            const uploadedImageIds: string[] = [];
            for (let i = 0; i < formData.images.length; i++) {
                const image = formData.images[i];
                try {
                    // 현재 이미지 업로드 진행 상황 업데이트
                    setUploadProgress(prev => ({
                        ...prev,
                        currentImageIndex: i + 1,
                        currentStep: `${getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtUploadProgress.txtImageUploading") || '이미지 업로드 중...'} (${i + 1}/${formData.images.length})`
                    }));

                    const response = await uploadImage(image.file);
                    if (response?.id) {
                        uploadedImageIds.push(response.id);
                    } else {
                        console.warn(`이미지 ${i + 1} 업로드 응답에 id가 없습니다:`, response);
                    }
                } catch (error) {
                    console.error(`이미지 ${i + 1} 업로드 실패:`, error);
                    alert(`${getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtUploadProgress.txtImageUpload") || '이미지'} ${i + 1} 업로드에 실패했습니다. 다시 시도해주세요.`);
                    return;
                }
            }

            // 2. 영상 파일 업로드
            let submitVideoId: string | undefined = uploadedVideoId || undefined;
            if (!submitVideoId && formData.video) {
                try {
                    // 영상 업로드 진행 상황 업데이트
                    setUploadProgress(prev => ({
                        ...prev,
                        isVideoUploading: true,
                        videoUploadProgress: 0,
                        currentStep: getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtUploadProgress.txtVideoUploading") || '영상 업로드 중...'
                    }));

                    console.log("영상 업로드 시작:", formData.video.name);
                    const response = await uploadVideoWithThumbnail(
                        formData.video,
                        videoWidth,
                        videoHeight,
                        false,
                        undefined,
                        true,
                        (progressEvent) => {
                            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                            console.log("영상 업로드 진행률:", percentCompleted + "%");
                            setUploadProgress(prev => ({
                                ...prev,
                                videoUploadProgress: percentCompleted
                            }));
                        }
                    );

                    console.log("영상 업로드 완료:", response);
                    if (response?.id) {
                        submitVideoId = response.id;
                        console.log("영상 ID:", submitVideoId);
                    } else {
                        console.warn("영상 업로드 응답에 id가 없습니다:", response);
                        alert(getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtVideoUploadError") || "영상 업로드에 실패했습니다. 다시 시도해주세요.");
                        return;
                    }
                } catch (error) {
                    console.error("영상 업로드 실패:", error);
                    alert(`${getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtVideoUploadError") || "영상 업로드에 실패했습니다"}: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
                    return;
                }
            }

            // 3. 다이어리 데이터 생성
            setUploadProgress(prev => ({
                ...prev,
                currentStep: getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtUploadProgress.txtDiarySaving") || '다이어리 저장 중...'
            }));

            const diaryData = {
                title: formData.title.trim(),
                content: formData.content.trim(),
                imageIds: uploadedImageIds.length > 0 ? uploadedImageIds : undefined,
                videoId: submitVideoId
            };

            const result = await createDiary(diaryData) as IDiaryCreateDataResult;
            console.log({ result });

            if (result?.data) {
                // 업로드 진행 상황 팝업 숨기기
                setUploadProgress(prev => ({ ...prev, isVisible: false }));
                alert(getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtDiarySaveSuccess") || "다이어리가 성공적으로 저장되었습니다!");
                // 폼 데이터 초기화
                setFormData({
                    title: "",
                    content: "",
                    images: [],
                    video: null
                });
                setUploadedVideoId(null);
                setUploadedVideoPreviewUrl(null);
                setVideoPreUploadProgress(0);
                navigate("/student/growth/diary");
            } else {
                throw new Error("다이어리 저장 응답 오류");
            }
        } catch (error) {
            console.error("다이어리 저장 실패:", error);
            alert(getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtDiarySaveError") || "다이어리 저장에 실패했습니다. 다시 시도해주세요.");
        } finally {
            setIsSubmitting(false);
            // 업로드 진행 상황 팝업 숨기기
            setUploadProgress(prev => ({ ...prev, isVisible: false }));
        }
    };

    const handleCancel = () => {
        if (window.confirm(getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtConfirmExit") || "작성 중인 내용이 사라집니다. 정말 나가시겠습니까?")) {
            navigate("/student/growth/diary");
        }
    };

    return (
        <main className="bg-gray-50 min-h-screen">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* 헤더 */}
                <div className="mb-8">
                    <div className="mb-4">
                        <button
                            onClick={handleCancel}
                            className="p-2 text-gray-600 hover:text-gray-900 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                    </div>
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                        {text.writeTitle}
                    </h1>
                    <p className="text-gray-600 text-sm sm:text-base">
                        {text.writeDesc}
                    </p>
                </div>

                {/* 다이어리 작성 폼 */}
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* 통합된 다이어리 작성 영역 */}
                    <div className="bg-white rounded-2xl shadow-sm p-6">
                        <div className="space-y-6">
                            {/* 제목 */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {text.title} *
                                </label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => handleInputChange("title", e.target.value)}
                                    placeholder={text.titlePlaceholder}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#CF3688] focus:border-transparent transition-all"
                                    required
                                />
                            </div>

                            {/* 내용 */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {text.content} *
                                </label>
                                <textarea
                                    value={formData.content}
                                    onChange={(e) => handleInputChange("content", e.target.value)}
                                    placeholder={text.contentPlaceholder}
                                    rows={8}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#CF3688] focus:border-transparent transition-all resize-none"
                                    required
                                />
                            </div>

                            {/* 영상 업로드 */}
                            <div>
                                <div className="flex justify-between items-center mb-4">
                                    <label className="text-sm font-medium text-gray-700">
                                        {text.videoAttachment}
                                        {
                                            (process.env.REACT_APP_ENV !== 'prod' ? "stage" : "")
                                        }
                                    </label>
                                    <span className="text-sm text-gray-500">
                                        {text.videoCount}
                                    </span>
                                </div>
                                <div className="space-y-4">
                                    {!formData.video ? (
                                        <button
                                            type="button"
                                            onClick={() => document.getElementById('videoInput')?.click()}
                                            className="w-full p-8 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#CF3688] hover:bg-[#CF3688]/5 transition-all text-center"
                                        >
                                            <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                            <p className="text-gray-600">{text.videoUploadHint}</p>
                                        </button>
                                    ) : (
                                        isVideoPreUploading ? (
                                            <div className="relative w-full rounded-lg border border-gray-200 bg-gray-50 p-6">
                                                <p className="text-sm text-gray-600 mb-3 text-center">
                                                    {text.videoUploading}
                                                </p>
                                                <div className="w-full bg-gray-200 rounded-full h-2">
                                                    <div
                                                        className="bg-[#CF3688] h-2 rounded-full transition-all duration-300"
                                                        style={{ width: `${videoPreUploadProgress}%` }}
                                                    />
                                                </div>
                                                <p className="text-xs text-gray-500 mt-2 text-right">{videoPreUploadProgress}%</p>
                                                <button
                                                    type="button"
                                                    onClick={handleRemoveVideo}
                                                    className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors flex items-center justify-center"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="relative">
                                                <video
                                                    key={uploadedVideoPreviewUrl ? "remote-preview" : (hasBlobPreviewError ? "blob-error" : "blob-preview")}
                                                    src={uploadedVideoPreviewUrl || (!hasBlobPreviewError ? videoUrl : null) || undefined}
                                                    controls
                                                    playsInline
                                                    className="w-full rounded-lg"
                                                    onError={(event) => {
                                                        handleVideoPreviewError(event.currentTarget);
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={handleRemoveVideo}
                                                    className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors flex items-center justify-center"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        )
                                    )}
                                    <input
                                        ref={videoInputRef}
                                        id="videoInput"
                                        type="file"
                                        accept="video/*"
                                        onChange={handleVideoUpload}
                                        className="hidden"
                                    />
                                </div>
                            </div>

                            {/* 이미지 업로드 */}
                            <div>
                                <div className="flex justify-between items-center mb-4">
                                    <label className="text-sm font-medium text-gray-700">
                                        {text.imageAttachment}
                                    </label>
                                    <span className="text-sm text-gray-500">
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.write.txtImageCount")?.replace('{count}', formData.images.length.toString()) || `(${formData.images.length}/10)`}
                                    </span>
                                </div>
                                <div className="space-y-4">
                                    {formData.images.length < 10 && (
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="w-full p-8 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#CF3688] hover:bg-[#CF3688]/5 transition-all text-center"
                                        >
                                            <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                            </svg>
                                            <p className="text-gray-600">{text.imageUploadHint}</p>
                                        </button>
                                    )}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        accept=".png,.jpg,.jpeg,image/png,image/jpg,image/jpeg"
                                        onChange={handleImageUpload}
                                        className="hidden"
                                    />

                                    {formData.images.length > 0 && (
                                        <div className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide space-x-3">
                                            {formData.images.map((image) => (
                                                <div key={image.id} className="flex-none w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 relative snap-center rounded-xl overflow-hidden">
                                                    <img
                                                        src={image.url}
                                                        alt={`Uploaded image`}
                                                        className="w-full h-full object-cover"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveImage(image.id)}
                                                        className="absolute top-0 right-0 p-1.5 bg-[#cf3688]/70 rounded-full text-white hover:bg-[#cf3688] transition-colors"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 알림 메시지 */}
                            <div className="p-3">
                                <div className="flex items-center">
                                    <svg className="w-5 h-5 text-[#CF3688] mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                    <p className="text-sm text-[#CF3688]">
                                        {text.validationMediaAlert}
                                    </p>
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* 안전한 캐스팅 환경 안내 메시지 */}
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                        <div className="flex items-start">
                            <svg className="w-5 h-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <p className="text-sm text-amber-800 leading-relaxed">
                                {text.safetyNotice}
                            </p>
                        </div>
                    </div>

                    {/* 제출 버튼 */}
                    <div className="flex gap-4 pt-6">
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                        >
                            {text.btnCancel}
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting || isVideoPreUploading}
                            className="flex-1 px-6 py-3 bg-[#CF3688] text-white rounded-xl hover:bg-[#CF3688]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {isVideoPreUploading
                                ? text.videoUploading
                                : isSubmitting ?
                                    text.btnSaving :
                                    text.btnSave
                            }
                        </button>
                    </div>
                </form>
            </div>

            <VideoUploadFlowPopup
                isVisible={uploadProgress.isVisible}
                phase={uploadProgress.phase}
                progress={
                    uploadProgress.isVideoUploading
                        ? uploadProgress.videoUploadProgress
                        : uploadProgress.totalImages > 0
                            ? Math.round(
                                (uploadProgress.currentImageIndex / uploadProgress.totalImages) * 100
                            )
                            : 0
                }
                currentStep={uploadProgress.currentStep}
                canCancel={uploadProgress.phase === "uploading" && uploadProgress.isVideoUploading}
                errorMessage={uploadProgress.errorMessage}
                thumbnails={uploadProgress.thumbnails}
                selectedIndex={uploadProgress.selectedIndex}
                isSaving={uploadProgress.isSaving}
                labels={{
                    close: text.popupClose,
                    save: text.popupSave,
                    uploading: text.popupPleaseWait,
                    thumbnailTitle: text.popupThumbnailTitle,
                }}
                onCancelUpload={handleCloseVideoUploadPopup}
                onClose={handleCloseVideoUploadPopup}
                onSave={handleSaveVideoThumbnail}
                onSelectThumbnail={handleSelectVideoThumbnail}
            />
        </main>
    );
};

export default WriteDiary;
